import moment from "moment";
import { useEffect, useRef, useState } from "react";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";
import { ReactComponent as SendLogo } from "../../Assets/paper-plane-top-solid.svg";
import BotActionInput from "../../Components/BotActionInput/BotActionInput";
import ChatMessage from "../../Components/ChatMessage/ChatMessage";
import ChatMessageComposer from "../../Components/ChatMessageComposer/ChatMessageComposer";
import CommunicationManager from "../../Helper/CommunicationManager";
import Communicator from "../../Helper/Communicator";
import { useLoadingTimeout } from "../../Helper/useTimeoutHook";
import { useConfigStore } from "../../Stores/Config";
import { useHelpCenterStore } from "../../Stores/HelpCenter";
import { useRouterStore } from "../../Stores/Router";
import { useSessionStore } from "../../Stores/Session";
import { ChannelTypes, useTicketStore } from "../../Stores/Ticket";
import "./Conversation.scss";

const actionsWithoutKai = ["buttons", "chatinput", "aitask"];

function Conversation() {
  const routerStore = useRouterStore();
  const feedbackItemStore = useTicketStore();
  const sessionStore = useSessionStore();
  const helpCenterStore = useHelpCenterStore();
  const ticketStore = useTicketStore();
  const configStore = useConfigStore();
  const [composerHeight, setComposerHeight] = useState(57);
  const [isDragOver, setIsDragOver] = useState(false);
  const [_, setDragCounter] = useState(0);
  const bottomRef = useRef(null as any);
  const composerRef = useRef<{ setFiles: (files: File[]) => void }>(null);
  const currentTicket = ticketStore.currentTicket;

  const isLoading =
    feedbackItemStore.loadingComments ||
    !feedbackItemStore.currentTicket ||
    (currentTicket?.bot?.active &&
      (feedbackItemStore?.currentComments ?? []).length === 0) ||
    (!ticketStore.currentTicket?.conversationClosed &&
      currentTicket?.bot?.active &&
      !feedbackItemStore.currentBotAction?.action);

  // Loading timeout fallback mechanism
  useLoadingTimeout(
    isLoading ?? false,
    () => {
      if (feedbackItemStore.currentTicket?.shareToken) {
        CommunicationManager.getInstance().reloadMainData();
      }
    },
    45000, // Wait 45 seconds initially
    10000  // Then reload every 10 seconds
  );

  const getComposerType = () => {
    if (isLoading) {
      return "none";
    }

    if (feedbackItemStore.currentTicket?.bot?.active) {
      const isAnswerBotActive =
        feedbackItemStore.currentBotAction?.action?.type === "answerbotflow" ||
        feedbackItemStore.currentBotAction?.action?.type === "aitask" ||
        feedbackItemStore.currentBotAction?.action?.type === "chatinput";
      if (isAnswerBotActive || !feedbackItemStore.currentBotAction?.action) {
        return "botTextInput";
      } else {
        return "botInput";
      }
    }

    return "textInput";
  };
  const composerType = getComposerType();
  const showTypingIndicator = ticketStore.typingUser || isLoading;
  const botWrittingText = ticketStore.botWrittingText;

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToBottomAfterFocus = () => {
    setTimeout(() => {
      scrollToBottom();
    }, 400);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    if (e?.dataTransfer?.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => {
      const newCounter = prev - 1;
      if (newCounter <= 0) {
        setIsDragOver(false);
      }
      return newCounter;
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setDragCounter(0);

      const files = Array.from(e?.dataTransfer?.files ?? []);
      if (files.length > 0 && composerRef.current) {
        composerRef.current.setFiles(files);
      }
    } catch { }
  };

  useEffect(() => {
    scrollToBottom();
  }, [showTypingIndicator, botWrittingText]);

  // Ref to store tippy instances for cleanup
  const tippyInstancesRef = useRef<any[]>([]);

  useEffect(() => {
    const articleDivs = document.querySelectorAll(
      ".helpcenter-conversation-article"
    );

    const handleClick = (event: any) => {
      if (
        event.target.tagName === "A" ||
        event.target.tagName === "svg" ||
        event.target.tagName === "path"
      ) {
        event.preventDefault();
        return;
      }

      const articleUrl = event.currentTarget.getAttribute("data-url");

      if (articleUrl) {
        event.preventDefault();
        try {
          const lastPathSegment =
            articleUrl.split("/").filter(Boolean).pop() || "";
          const docId = lastPathSegment.split("-")[0];
          if (docId) {
            helpCenterStore.openArticle(docId, true);
          } else {
            Communicator.openURL(articleUrl, false);
          }
        } catch (error) {
          Communicator.openURL(articleUrl, false);
        }
      }
    };

    // Clean up existing tippy instances
    tippyInstancesRef.current.forEach((instance) => {
      if (instance && typeof instance.destroy === "function") {
        instance.destroy();
      }
    });
    tippyInstancesRef.current = [];

    // Initialize tippy only on new ai-annotation elements
    const annotationElements = document.querySelectorAll(".ai-annotation");
    annotationElements.forEach((element) => {
      // Check if element already has tippy instance
      if (!(element as any)._tippy) {
        const instance = tippy(element, {
          theme: "light",
          allowHTML: true,
          duration: [300, 200], // [show, hide] duration in ms
          delay: [200, 0], // [show, hide] delay in ms
          interactive: true, // Allows hovering over the tooltip
          content(reference) {
            const title = reference.getAttribute("data-title");
            const url = reference.getAttribute("href");
            let baseUrl = "";

            try {
              baseUrl = url ? new URL(url)?.hostname : "";
            } catch (error) { }

            return `<div class="ai-annotation-tooltip">
              <a href="${url}" target="_blank">
                <div class="ai-annotation-tooltip-url">
                  <div class="ai-annotation-tooltip-url-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path d="M579.8 267.7c56.5-56.5 56.5-148 0-204.5c-50-50-128.8-56.5-186.3-15.4l-1.6 1.1c-14.4 10.3-17.7 30.3-7.4 44.6s30.3 17.7 44.6 7.4l1.6-1.1c32.1-22.9 76-19.3 103.8 8.6c31.5 31.5 31.5 82.5 0 114L422.3 334.8c-31.5 31.5-82.5 31.5-114 0c-27.9-27.9-31.5-71.8-8.6-103.8l1.1-1.6c10.3-14.4 6.9-34.4-7.4-44.6s-34.4-6.9-44.6 7.4l-1.1 1.6C206.5 251.2 213 330 263 380c56.5 56.5 148 56.5 204.5 0L579.8 267.7zM60.2 244.3c-56.5 56.5-56.5 148 0 204.5c50 50 128.8 56.5 186.3 15.4l1.6-1.1c14.4-10.3 17.7-30.3 7.4-44.6s-30.3-17.7-44.6-7.4l-1.6 1.1c-32.1 22.9-76 19.3-103.8-8.6C74 372 74 321 105.5 289.5L217.7 177.2c31.5-31.5 82.5-31.5 114 0c27.9 27.9 31.5 71.8 8.6 103.9l-1.1 1.6c-10.3 14.4-6.9 34.4 7.4 44.6s34.4 6.9 44.6-7.4l1.1-1.6C433.5 260.8 427 182 377 132c-56.5-56.5-148-56.5-204.5 0L60.2 244.3z"/></svg>
                  </div>
                  <div class="ai-annotation-tooltip-url-text">${baseUrl}</div>
                </div>
                <div class="ai-annotation-tooltip-title">${title}</div>
              </a>
            </div>`;
          },
        });
        tippyInstancesRef.current.push(instance);
      }
    });

    articleDivs.forEach((articleDiv) => {
      articleDiv.addEventListener("click", handleClick);
    });

    return () => {
      // Clean up tippy instances
      tippyInstancesRef.current.forEach((instance) => {
        if (instance && typeof instance.destroy === "function") {
          instance.destroy();
        }
      });
      tippyInstancesRef.current = [];

      // Clean up event listeners
      articleDivs.forEach((articleDiv) => {
        articleDiv.removeEventListener("click", handleClick);
      });
    };
  }, [feedbackItemStore.currentComments]);

  useEffect(() => {
    if (routerStore.currentData && routerStore.currentData.shareToken) {
      feedbackItemStore.loadCurrentTicket(routerStore.currentData.shareToken);
    }
  }, [routerStore.currentData, ticketStore.previousTickets]);

  useEffect(() => {
    return () => {
      feedbackItemStore.clearCurrentTicket();
      feedbackItemStore.setCurrentComposerText("");
    };
  }, []);

  useEffect(() => {
    if (ticketStore.currentTicket?.sessionNotificationsUnread) {
      ticketStore.setTicketUnread(false);
    }
  }, [ticketStore.currentTicket]);

  useEffect(() => {
    scrollToBottom();
  }, [
    feedbackItemStore.currentComments,
    feedbackItemStore.currentBotAction,
    ticketStore.currentTicket?.conversationClosed,
    ticketStore.currentTicket?.type,
    ticketStore?.botWrittingText,
  ]);

  const renderMessageComposer = () => {
    if (composerType !== "textInput") {
      return;
    }

    const currentTicket = ticketStore.currentTicket;

    if (currentTicket?.conversationClosed) {
      return null;
    }

    if (
      currentTicket?.channel?.type === ChannelTypes.FACEBOOK ||
      currentTicket?.channel?.type === ChannelTypes.INSTAGRAM ||
      currentTicket?.channel?.type === ChannelTypes.WHATSAPP
    ) {
      return null;
    }

    return (
      <ChatMessageComposer
        ref={composerRef}
        onFocus={() => {
          scrollToBottomAfterFocus();
        }}
        onBlur={() => {
          scrollToBottomAfterFocus();
        }}
        typingStatusChanged={(isTyping) => {
          ticketStore.isTyping(isTyping);
        }}
        onHeightUpdated={(height) => {
          setComposerHeight(height);
        }}
        sendMessage={(message, attachments) => {
          feedbackItemStore.sendComment(message, attachments);
        }}
      />
    );
  };

  const renderAnswerBotMessageComposer = () => {
    if (composerType !== "botTextInput") {
      return null;
    }

    if (ticketStore.currentTicket?.conversationClosed) {
      return null;
    }

    const botIsWriting = botWrittingText && botWrittingText.length > 0;
    const botIsTyping = showTypingIndicator;

    return (
      <ChatMessageComposer
        ref={composerRef}
        answerBotIsActive
        onHeightUpdated={(height) => {
          setComposerHeight(height);
        }}
        onFocus={() => {
          scrollToBottomAfterFocus();
        }}
        onBlur={() => {
          scrollToBottomAfterFocus();
        }}
        disabled={botIsWriting || botIsTyping}
        sendMessage={(message, files) => {
          if (
            feedbackItemStore.currentBotAction?.action?.type === "chatinput"
          ) {
            ticketStore.answerBotAction(
              {
                text: message,
                message: message,
              },
              files
            );
          } else {
            ticketStore.answerBotAction(
              {
                question: message,
                message: message,
                aiTools: configStore.aiTools,
                isFirstMessage: !feedbackItemStore.hasAnswerBotReply(),
              },
              files
            );
          }
        }}
      />
    );
  };

  const renderConversationClosed = () => {
    if (!ticketStore.currentTicket?.conversationClosed) {
      return null;
    }

    return (
      <div className="conversation-ended">
        <div className="conversation-ended-label">
          {configStore.translateText("conversationEnded")}
        </div>
        {configStore.config.hideAllConversations ? null : (
          <div
            className="action-button form-send-button"
            onClick={() => {
              ticketStore.createNewConversation({
                isManualStart: true,
              });
            }}
          >
            {configStore.translateText("sendMessage")}
            <SendLogo />
          </div>
        )}
      </div>
    );
  };

  const renderDisabledTicket = () => {
    const currentTicket = ticketStore.currentTicket;

    if (
      currentTicket?.channel?.type !== ChannelTypes.FACEBOOK &&
      currentTicket?.channel?.type !== ChannelTypes.INSTAGRAM &&
      currentTicket?.channel?.type !== ChannelTypes.WHATSAPP
    ) {
      return null;
    }

    const getChannelName = () => {
      switch (currentTicket?.channel?.type) {
        case ChannelTypes.FACEBOOK:
          return "Facebook";

        case ChannelTypes.INSTAGRAM:
          return "Instagram";

        case ChannelTypes.WHATSAPP:
          return "WhatsApp";
        default:
          return "Channel";
      }
    };

    const label = configStore
      .translateText("wrongConversationChannel")
      .replace("{channel}", getChannelName());

    return (
      <div className="conversation-ended">
        <div className="conversation-ended-label">{label}</div>
        {configStore.config.hideAllConversations ? null : (
          <div
            className="action-button form-send-button"
            onClick={() => {
              ticketStore.createNewConversation({
                isManualStart: true,
              });
            }}
          >
            {configStore.translateText("sendMessage")}
            <SendLogo />
          </div>
        )}
      </div>
    );
  };

  var bottomToolbarHeight =
    composerType === "textInput" || composerType === "botTextInput"
      ? composerHeight
      : 0;
  if (ticketStore.currentTicket?.conversationClosed) {
    bottomToolbarHeight = 140;
  }

  const sts = configStore.config?.sts ?? false;
  let showStatus = true;
  if (
    !ticketStore.currentTicket?.type ||
    ticketStore.currentTicket?.type === "INQUIRY" ||
    ticketStore.currentTicket?.type === "BOT" ||
    !sts
  ) {
    showStatus = false;
  }

  return (
    <div
      className={`conversation ${feedbackItemStore?.currentTicket?.type !== "INQUIRY" &&
        feedbackItemStore?.currentTicket?.type !== "BOT"
        ? "conversation--ticket"
        : ""
        } ${showStatus ? "conversation--sts" : ""} ${isDragOver ? "conversation--drag-over" : ""
        }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div
        className="conversation-messages"
        style={{
          height: `100%`,
          paddingBottom: `${bottomToolbarHeight + 45}px`,
        }}
      >
        {!feedbackItemStore?.currentTicket?.hideContent && (
          <ChatMessage
            key="initial"
            chatMessage={{
              type: "FEEDBACK_ITEM",
              data: feedbackItemStore.currentTicket,
              createdAt: feedbackItemStore.currentTicket?.createdAt,
            }}
            isCreator={true}
            showTime={true}
            lastOfGroup={true}
          />
        )}
        {feedbackItemStore.currentComments &&
          feedbackItemStore.currentComments
            .filter((comment) => {
              if (comment.type === "BOT" && comment.data?.action) {
                return false;
              }
              if (comment.data?.hiddenAction) {
                return false;
              }
              if (
                comment.type === "BOT" &&
                comment.data?.content?.length < 1 &&
                !comment.data?.showReplyTimes
              ) {
                return false;
              }

              return true;
            })
            .map((comment, i, currentComments) => {
              var nextComment = null;
              var isLastInSection = false;
              var showTime = true;
              if (i < currentComments.length) {
                nextComment = currentComments[i + 1];
              }

              if (!nextComment) {
                isLastInSection = true;
              }

              if (comment.session && nextComment?.session === comment.session) {
                isLastInSection = true;
              }

              if (comment.user && nextComment?.user === comment.user) {
                isLastInSection = true;
              }

              if (comment.type !== nextComment?.type) {
                isLastInSection = true;
              }

              if (
                comment.type === "BOT" &&
                nextComment?.type === "BOT_INPUT_REPLY"
              ) {
                isLastInSection = false;
              }

              if (
                comment.type === "BOT_INPUT_REPLY" &&
                nextComment?.type === "BOT_INPUT_REPLY"
              ) {
                isLastInSection = false;
              }

              const activeActionType =
                ticketStore.currentBotAction?.action?.type;
              if (
                comment.type === "BOT" &&
                !nextComment &&
                activeActionType &&
                !actionsWithoutKai.includes(activeActionType)
              ) {
                isLastInSection = false;
                showTime = false;
              }

              if (nextComment && !isLastInSection) {
                var duration = moment.duration(
                  moment(nextComment.createdAt).diff(comment.createdAt)
                );
                var minutes = duration.asMinutes();
                if (minutes < 30) {
                  showTime = false;
                }
              }

              var isCreator = false;
              if (comment.session?.gleapId === sessionStore?.session?.gleapId) {
                isCreator = true;
              }

              return (
                <ChatMessage
                  key={comment.id}
                  chatMessage={comment}
                  showTime={showTime}
                  isCreator={isCreator}
                  lastOfGroup={isLastInSection}
                  imageLoaded={scrollToBottom}
                />
              );
            })}
        {(composerType === "botInput" || composerType === "botTextInput") && (
          <BotActionInput key="bot-action-input" />
        )}
        {showTypingIndicator && !botWrittingText && (
          <ChatMessage
            key="typing"
            lastOfGroup={true}
            chatMessage={{
              data: {
                content: "Typing…",
              },
              user: ticketStore.typingUser,
              type: "TEXT",
            }}
            forceTyping={true}
            showTime={false}
          />
        )}
        {botWrittingText && botWrittingText.length > 0 && (
          <ChatMessage
            key="botwritting"
            lastOfGroup={true}
            chatMessage={{
              data: {
                answerBot: true,
                hasAnswer: true,
                isWritting: true,
                sources: [],
                content: botWrittingText,
              },
              user: undefined,
              session: undefined,
              type: "BOT",
            }}
            showTime={false}
          />
        )}
        <div ref={bottomRef} />
      </div>
      {renderAnswerBotMessageComposer()}
      {renderMessageComposer()}
      {renderConversationClosed()}
      {renderDisabledTicket()}
      {isDragOver && (
        <div className="conversation-drag-overlay">
          <div className="conversation-drag-overlay-content">
            <div className="conversation-drag-overlay-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14,2 14,8 20,8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10,9 9,9 8,9"></polyline>
              </svg>
            </div>
            <div className="conversation-drag-overlay-title">
              Drop files here to attach
            </div>
            <div className="conversation-drag-overlay-subtitle">
              Release to upload your files
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Conversation;
